import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";
import { getUserType } from "../hooks/useLocalStorage";
import { CssBaseline } from "@mui/material";
import { useSimulation } from "../context/SimulationContext";

const CustomTheme = ({ children }) => {
  const { studentRoleData } = useSimulation();
  const userRole = getUserType(); // from localstorage data

  const userType = studentRoleData ?? userRole;
const isStudent = userType === "student"
  const Theme = createTheme({
    typography: {
      fontFamily: ["Karla", "Rubik"].join(","),
      h1: {
        fontWeight: 800,
        lineHeight: 80 / 64,
        fontSize: "10rem",
        color: "white",
      },
      h2: {
        fontWeight: 800,
        lineHeight: 64 / 48,
        fontSize: "2rem",
      },
      h3: {
        // fontWeight: 700,
        lineHeight: 1.5,
        fontSize: "1.5rem",
        color: "white",
      },
      h4: {
        fontWeight: 300,
        fontSize: "1.5rem",
        fontFamily: "Karla",
        "@media (min-width:600px)": {
          fontSize: "1.75rem",
        },
        "@media (min-width:900px)": {
          fontSize: "2rem",
        },
      },
      h5: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: "1.125rem",
        color: "white",
      },
      h6: {
        lineHeight: 28 / 18,
        fontSize: "1.25rem",
        "@media (max-width:599px)": {
          fontSize: "1.1rem",
        },
        fontWeight: 300,
      },
      subtitle1: {
        // fontWeight: 600,
        lineHeight: 1.5,
        fontSize: "1rem", // Default for sm+ screens
        "@media (max-width:599px)": {
          // xs screens
          fontSize: "0.875rem",
        },
      },
      subtitle2: {
        fontWeight: 300,
        lineHeight: 22 / 14,
        fontSize: "0.875rem",
        color: "#000000ff",
      },
      body1: {
        lineHeight: 1.6,
        fontSize: {
          xs: "0.875rem",
          sm: "1rem",
        },
        fontFamily: "Rubik",
      },
      body2: {
        lineHeight: 1.0,
        fontSize: {
          xs: 10,
          sm: 12,
        },
        color: "#212121",
        fontFamily: "Rubik",
      },
      caption: {
        lineHeight: 1.5,
        fontSize: "0.75rem",
        color: "black",
      },
      overline: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: "0.75rem",
        textTransform: "uppercase",
      },
      button: {
        fontWeight: 700,
        lineHeight: 24 / 14,
        fontSize: "0.875rem",
        textTransform: "unset",
      },
      heading1: {
        fontWeight: 700,
        lineHeight: "120%",
        fontSize: "2rem",
      },
      heading2: {
        fontWeight: 600,
        lineHeight: "120%",
        fontSize: "1.75rem",
      },
      titleLarge: {
        fontWeight: 500,
        lineHeight: "120%",
        fontSize: "1.5rem",
      },
      titleMedium: {
        fontWeight: 500,
        lineHeight: "120%",
        fontSize: "1.25rem",
      },
      titleSmall: {
        fontWeight: 500,
        lineHeight: "120%",
        fontSize: "1.125rem",
      },
      bodyLarge: {
        fontWeight: 400,
        lineHeight: "130%",
        fontSize: "1rem",
      },
      bodyMedium: {
        fontWeight: 400,
        lineHeight: "140%",
        fontSize: "0.875rem",
      },
      bodySmall: {
        fontWeight: 400,
        lineHeight: "140%",
        fontSize: "0.75rem",
      },
    },

    palette: {
      primary: {
        main:isStudent ? "#80278C" : "#51B0CF", // --primary
        light: "#EBD7EF",
        secondary: "#9c50ac",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: isStudent ? "#80278C" : "#51B0CF", // --accent
        light: "#CCF1FF", // --accent-light
        contrastText: "#FFFFFF",
        blueLight:"#51c9f1ff"
      },
      text: {
        primary: "#000000",
        contrastText: "#FFFFFF",
        // --text
      },
      background: {
        default: "#FFFFFF", // --background
        paper: "#F5F5F5",
      },
      error: {
        main: "#b71c1c",
        light:"#d84747ff"
      },
      custom: {
        disabled: "#D1D1D1", // --disabled
        selectedMenuBg: "#000000",
        selectedMenuText: "#FFFFFF",
        unselectedMenuBg: "#FFFFFF",
        unselectedMenuText: "#000000",
        blue100: "#CCF1FF",
        blue200: "#99E3FF",
        blue300: "#66D5FF",
        blue400: "#33C7FF",
        blue500: "#00BFFF",
        blue700: "#009ACC",
        blue900: "#006688",
        tools:"#88cceaff",
        info:"#3CBAF0",
        output:"#f5f7fa",
        boxcolor:"#e0e0e0",
        textgrey:"#5E5959",
        gradient: "linear-gradient(45deg, #a14ab8 30%, #80278C 90%)",
        gradientReverse: "linear-gradient(to left, #00BFFF, #80278C)",
      },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: isStudent ? "#80278C" : "#51B0CF",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#EBD7EF",
              color: isStudent ? "#80278C" : "#51B0CF",
            },
          },
          containedSecondary: {
            backgroundColor: "#00BFFF",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#CCF1FF",
              color: "#006688",
            },
          },
          // disabled: {
          //   backgroundColor: "#D1D1D1",
          //   color: "#000000",
          // },

          "&.Mui-disabled": {
            backgroundColor: "#D1D1D1",
            color: "#000000",
          },
          root: {
            fontFamily: "Karla, sans-serif",
            fontSize: "1rem",
            textTransform: "none",
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: isStudent ? "#80278C" : "#51B0CF",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: isStudent ? "#80278C" : "#51B0CF",
              },
            },
            "&:not(.Mui-selected)": {
              backgroundColor: "#FFFFFF",
              color: "#000000",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            },
          },
        },
      },
    },
    custom: {
      gradient: "linear-gradient(0.25turn, #68CDF9, #078DEE)",
    },
  });

  const theme = responsiveFontSizes(Theme);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>{children}</CssBaseline>
    </ThemeProvider>
  );
};

export default CustomTheme;
